import React from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import { SPOTIFY_HEADER_SOURCES, type SpotifyHeaderSource } from 'fym-shared/src/music24/spotifyHeader';
import { TypedMemo } from '../../../../common/types';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import { DetailField } from '../details/DetailField';
import { PickTile, type PickTileOff } from '../details/imageTiles/PickTile';
import { SpotifyHeaderFrame, type HeaderPreviewGround } from './SpotifyHeaderFrame';
import { spotifyHeaderMessages as messages } from './spotifyHeaderMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Spotify header; each part uses its own slice of it */
import styles from './SpotifyHeader.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The name and the benefit line of a source tile. */
export type SourceTileNames = { name: MessageDescriptor; benefit: MessageDescriptor };

const NAMES: Readonly<Record<SpotifyHeaderSource, SourceTileNames>> = {
  cover: { name: messages.sourceCover, benefit: messages.sourceCoverBenefit },
  photo: { name: messages.sourcePhoto, benefit: messages.sourcePhotoBenefit },
  bank: { name: messages.sourceBank, benefit: messages.sourceBankBenefit },
  ai: { name: messages.sourceAi, benefit: messages.sourceAiBenefit },
  theme: { name: messages.sourceTheme, benefit: messages.sourceThemeBenefit },
};

interface HeaderSourceTilesProps {
  value: SpotifyHeaderSource;
  colours: ThemeColours;
  /** The ground of each tile: the cover, the profile photo, the first bank and AI picture. */
  grounds: Readonly<Record<SpotifyHeaderSource, HeaderPreviewGround>>;
  /** Why a tile is off (the song has no AI pictures yet), with its link. */
  off?: Partial<Record<SpotifyHeaderSource, PickTileOff>>;
  onChange: (source: SpotifyHeaderSource) => void;
  /** The sources to show, in this order. Default: the five sources of the header. */
  sources?: readonly SpotifyHeaderSource[];
  /** Another name or benefit for a source (the artist photo: "Release cover"). */
  names?: Partial<Record<SpotifyHeaderSource, SourceTileNames>>;
  /** The well of a tile. Default: a small Spotify header with the ground of the source. */
  renderPreview?: (source: SpotifyHeaderSource, narrow: boolean) => React.ReactNode;
  /** The hint of the field. Default: the hint of the header. */
  hint?: string;
  /** The prefix of the tile test ids. Default `header-source`. */
  testIdPrefix?: string;
}

/**
 * "Picture" of step 3 (design spotify-header a1): the five sources as tiles with a small header each.
 * The artist Profile tools (design artist-profile b1–b4) give their own sources, names and wells.
 */
const HeaderSourceTilesComponent: React.FC<HeaderSourceTilesProps> = ({
  value,
  colours,
  grounds,
  off,
  onChange,
  sources = SPOTIFY_HEADER_SOURCES,
  names,
  renderPreview,
  hint,
  testIdPrefix = 'header-source',
}) => {
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const nameOf = (source: SpotifyHeaderSource): SourceTileNames => names?.[source] ?? NAMES[source];
  return (
    <DetailField
      title={intl.formatMessage(messages.pictureTitle)}
      hint={hint ?? intl.formatMessage(messages.pictureHint)}
    >
      <div className={styles.sourceTiles} role="radiogroup" aria-label={intl.formatMessage(messages.pictureTitle)}>
        {sources.map((source) => (
          <PickTile
            key={source}
            testId={`${testIdPrefix}-${source}`}
            name={intl.formatMessage(nameOf(source).name)}
            isOn={source === value}
            small
            preview={
              renderPreview === undefined ? (
                <SpotifyHeaderFrame width={narrow ? 136 : 116} view="file" ground={grounds[source]} colours={colours} />
              ) : (
                renderPreview(source, narrow)
              )
            }
            benefit={narrow ? undefined : intl.formatMessage(nameOf(source).benefit)}
            off={off?.[source]}
            onPick={() => onChange(source)}
          />
        ))}
      </div>
    </DetailField>
  );
};

export const HeaderSourceTiles = TypedMemo(HeaderSourceTilesComponent);
